import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Save, Camera, Video, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaPricingTab = () => {
  const [pricing, setPricing] = useState({
    photo_price_sub: 10,
    photo_price_nonsub: 20,
    video_first_sub: 30,
    video_extra_sub: 15,
    video_first_nonsub: 50,
    video_extra_nonsub: 25,
    video_gallery_price_sub: 10,
    video_gallery_price_nonsub: 20,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/admin/media-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPricing(res.data);
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/api/admin/media-pricing`, pricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(res.data);
      toast.success('Media pricing saved');
    } catch {
      toast.error('Failed to save pricing');
    }
    setSaving(false);
  };

  const fld = (key) => ({
    type: 'number',
    min: 0,
    step: 0.01,
    value: pricing[key],
    onChange: (e) => setPricing(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 })),
    'data-testid': `mp-${key}`,
  });

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="media-pricing-tab">
      <div>
        <h3 className="font-display font-bold text-lg uppercase tracking-tight">Media Purchase Pricing</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Set prices for one-off photo purchases and custom video edits. Logged-in subscribers automatically get subscriber pricing. Coaches and admins never pay.
        </p>
      </div>

      {/* Photos */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-primary">
            <Camera className="w-5 h-5" />
            <h4 className="font-display font-bold uppercase text-sm tracking-wide">Photo Pricing</h4>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Non-Subscriber (CAD)</Label>
              <Input {...fld('photo_price_nonsub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $20 per photo</p>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-primary"><Users className="w-3 h-3" /> Subscriber (CAD)</Label>
              <Input {...fld('photo_price_sub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $10 per photo</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Videos */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-primary">
            <Video className="w-5 h-5" />
            <h4 className="font-display font-bold uppercase text-sm tracking-wide">Gallery Video Pricing (per video)</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Applies to any event video you&apos;ve turned on <b>For Sale</b> in the Event Videos tab. Videos default to NOT for sale — flip each one on individually.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Non-Subscriber (CAD)</Label>
              <Input {...fld('video_gallery_price_nonsub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $20 per video</p>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-primary"><Users className="w-3 h-3" /> Subscriber (CAD)</Label>
              <Input {...fld('video_gallery_price_sub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $10 per video</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Video Orders */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-primary">
            <Video className="w-5 h-5" />
            <h4 className="font-display font-bold uppercase text-sm tracking-wide">Custom Video Order Pricing</h4>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Non-Sub: First Video</Label>
              <Input {...fld('video_first_nonsub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $50</p>
            </div>
            <div>
              <Label>Non-Sub: Each Additional</Label>
              <Input {...fld('video_extra_nonsub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $25</p>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-primary"><Users className="w-3 h-3" /> Sub: First Video</Label>
              <Input {...fld('video_first_sub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $30</p>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-primary"><Users className="w-3 h-3" /> Sub: Each Additional</Label>
              <Input {...fld('video_extra_sub')} />
              <p className="text-xs text-muted-foreground mt-1">Default: $15</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-media-pricing-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Pricing'}
      </Button>
    </div>
  );
};

export default MediaPricingTab;
